import {ref} from 'vue';
import {useRuntimeConfig} from '#app';
import {useAppleSdk} from '~/composables/useAppleSdk.js';
import {useFacebookSdk} from '~/composables/useFacebookSdk.js';
const GOOGLE_LOADING_QS = 'gLoading';
import {useValidation} from "~/composables/useValidation.js";
import {initProvidersIndependently} from "~/composables/useSocialProviders.js";

export const useSocialLogin = ({onGoogleAuth, onAppleAuth, onFacebookAuth} = {}) => {
    const config = useRuntimeConfig();

    // SDK readiness
    const isAppleReady = ref(false);
    const isFacebookReady = ref(false);
    // Providers whose setup threw. A button that cannot work must not be
    // offered as though it can; see useSocialProviders.js.
    const providerProblems = ref([]);

    // Loading states
    const googleLoading = ref(true);
    const appleLoading = ref(false);
    const facebookLoading = ref(false);

    let popupWindow = null;
    let googleLoginMessageHandler = null;
    let googleLoginStorageInterval = null;
    let googleLoginPopupInterval = null;

    let appleSuccessHandler = null;
    let appleFailureHandler = null;

    const {getSdk: getAppleSdk, loading: appleLoadingState} = useAppleSdk();
    const {getSdk: getFacebookSdk, loading: facebookLoadingState, error: facebookError} = useFacebookSdk();

    // --- GOOGLE ---------------------------------------------------
    const loginWithGoogle = () => {
        googleLoading.value = true;

        const clientId    = config.public.googleClientId;
        const redirectUri = `${window.location.origin}/auth/google/callback`;
        const nonce       = crypto.randomUUID();

        // Remember current location so we can return to it
        const state = encodeURIComponent(
            window.location.pathname +
            window.location.search +
            window.location.hash ||
            '/'
        );

        const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
        url.searchParams.set('client_id', clientId);
        url.searchParams.set('redirect_uri', redirectUri);
        url.searchParams.set('response_type', 'id_token');
        url.searchParams.set('scope', 'openid email profile');
        url.searchParams.set('nonce', nonce);
        url.searchParams.set('prompt', 'select_account');
        url.searchParams.set('state', state);

        const { isMobile } = useValidation();

        // Desktop → popup
        if (!isMobile()) {
            popupWindow = window.open(url.toString(), '_blank', 'width=500,height=600');

            // If popup blocked, fall back to redirect
            if (!popupWindow) {
                window.location.href = url.toString();
                return;
            }

            googleLoginPopupInterval = setInterval(() => {
                if (!popupWindow || popupWindow.closed) {
                    clearInterval(googleLoginPopupInterval);
                    googleLoginPopupInterval = null;
                    if (googleLoading.value) {
                        googleLoading.value = false;
                        console.warn('Google login popup was closed by the user');
                    }
                }
            }, 500);

            return; // keep loading spinner in desktop flow
        }

        // Mobile → full‑page redirect
        window.location.href = url.toString();
        googleLoading.value = false; // page unloads right after
    };


    const handleGoogleIdToken = async (idToken) => {
        try {
            popupWindow?.close();
        } catch (e) {
            // Ignore if blocked by browser security
        }
        if (googleLoginPopupInterval) {
            clearInterval(googleLoginPopupInterval);
            googleLoginPopupInterval = null;
        }
        if (onGoogleAuth && idToken) {
            await onGoogleAuth({provider: 'google', idToken});
        }
        googleLoading.value = false;
    };
    const handleFacebookLoginResponse = async (response) => {
        if (response.authResponse) {
            try {
                await onFacebookAuth?.({
                    provider: 'facebook',
                    accessToken: response.authResponse.accessToken
                });
            } catch (e) {
                console.error('Error during onFacebookAuth:', e);
            }
        }
        facebookLoading.value = false;
    };
    // --- FACEBOOK ---
    const loginWithFacebook = async () => {
        facebookLoading.value = true;
        const FB = await getFacebookSdk();
        if (!FB) {
            facebookLoading.value = false;
            return;
        }
        FB.getLoginStatus(async (statusResponse) => {
            if (statusResponse.status === 'connected') {
                // ✅ Already logged in — just use the token
                handleFacebookLoginResponse(statusResponse);
            } else {
                // 🆕 Not logged in — call FB.login
                FB.login((loginResponse) => {
                        handleFacebookLoginResponse(loginResponse);
                    },
                    { scope: 'public_profile,email', auth_type: 'rerequest' } // optional: 'auth_type' avoids denied perms
                );
            }
        });
    };

    // --- APPLE ---
    const loginWithApple = () => {
        appleLoading.value = true;
        if (window.AppleID?.auth) {
            window.AppleID.auth.signIn();
        }
    };

    const handleAppleLoginSuccess = async (data) => {
        const token = data.authorization.id_token;
        if (token) {
            await onAppleAuth?.({
                provider: 'apple',
                idToken: token,
                firstName: data.user?.name?.firstName,
                lastName: data.user?.name?.lastName
            });
        }
        appleLoading.value = false;
    };

    const handleAppleLoginError = () => {
        appleLoading.value = false;
    };

    // --- INIT & DESTROY ---
    const init = async () => {
        if (!process.client) {
            return;
        }

        // One provider failing must cost that provider only. Apple's init
        // throws when its client id is missing, and it used to abort this whole
        // function: the Google listeners below were never registered and
        // googleLoading stayed true, leaving the Google button in a permanent
        // spinner. See useSocialProviders.js.
        try {
            const {failed} = await initProvidersIndependently([
                ['apple', async () => {
                    const AppleID = await getAppleSdk();
                    isAppleReady.value = !appleLoadingState.value;

                    if (AppleID?.auth) {
                        AppleID.auth.init({
                            clientId: config.public.appleClientId,
                            scope: 'name email',
                            redirectURI: window.location.origin + '/auth/apple/callback',
                            state: Date.now().toString(),
                            usePopup: true
                        });
                    }

                    appleSuccessHandler = (event) => {
                        handleAppleLoginSuccess(event.detail);
                    };
                    appleFailureHandler = (event) => {
                        handleAppleLoginError(event.detail);
                    };

                    document.addEventListener('AppleIDSignInOnSuccess', appleSuccessHandler);
                    document.addEventListener('AppleIDSignInOnFailure', appleFailureHandler);
                }],
                ['facebook', async () => {
                    await getFacebookSdk();
                    isFacebookReady.value = !facebookLoadingState.value && !facebookError.value;
                }],
                ['google', async () => {
                    googleLoginMessageHandler = async (event) => {
                        if (event.origin !== window.origin || event.data?.type !== 'google-id-token') return;
                        await handleGoogleIdToken(event.data.token);
                    };
                    window.addEventListener('message', googleLoginMessageHandler);

                    if ('BroadcastChannel' in window) {
                        const bc = new BroadcastChannel('google-auth');
                        bc.onmessage = async (event) => {
                            if (event.data?.type === 'google-id-token') {
                                bc.close();
                                await handleGoogleIdToken(event.data.token);
                            }
                        };
                    }

                    googleLoginStorageInterval = setInterval(async () => {
                        const token = localStorage.getItem('google-id-token');
                        if (token) {
                            localStorage.removeItem('google-id-token');
                            clearInterval(googleLoginStorageInterval);
                            await handleGoogleIdToken(token);
                        }
                    }, 500);
                }],
            ]);
            providerProblems.value = failed;
            if (failed.includes('apple')) isAppleReady.value = false;
            if (failed.includes('facebook')) isFacebookReady.value = false;
        } finally {
            // googleLoading starts TRUE, so anything that escapes above would
            // strand the button in its spinner. Cleared here whatever happened.
            googleLoading.value = false;
        }
    };

    const destroy = () => {
        if (!process.client) {
            return;
        }

        if (googleLoginMessageHandler) {
            window.removeEventListener('message', googleLoginMessageHandler);
        }

        if (googleLoginStorageInterval) {
            clearInterval(googleLoginStorageInterval);
        }

        if (googleLoginPopupInterval) {
            clearInterval(googleLoginPopupInterval);
            googleLoginPopupInterval = null;
        }

        if (appleSuccessHandler) {
            document.removeEventListener('AppleIDSignInOnSuccess', appleSuccessHandler);
        }
        if (appleFailureHandler) {
            document.removeEventListener('AppleIDSignInOnFailure', appleFailureHandler);
        }
    };

    return {
        // SDK readiness
        isAppleReady,
        isFacebookReady,

        // loading states
        googleLoading,
        appleLoading,
        facebookLoading,
        providerProblems,

        // actions
        loginWithGoogle,
        loginWithApple,
        loginWithFacebook,
        init,
        destroy
    };
};
